import { z, type ZodType } from 'zod';
import { ORIGENES_POR_DEFECTO, origenesSchema } from './dominio/origenes';
import type { Permiso } from './permisos';
import { hora } from './schemas/comunes';
import { coincideBusqueda } from './texto';

/** Tipo de editor que la UI debe mostrar para cada configuración. */
export type EditorConfig = 'entero' | 'decimal' | 'booleano' | 'opciones' | 'hora' | 'texto' | 'lista_montos' | 'origenes';

export interface DefConfig<T> {
  categoria: string;
  titulo: string;
  descripcion: string;
  schema: ZodType<T>;
  porDefecto: T;
  editor: EditorConfig;
  permisoEdicion: Permiso;
  /** Unidad mostrada junto al valor (min, días, %, MXN…). */
  unidad?: string;
  opciones?: { valor: string | number; etiqueta: string }[];
  /** Regla entre el valor guardado y el nuevo; devuelve el mensaje de error o `null`. */
  validarCambio?: (anterior: T, nuevo: T) => string | null;
}

function def<T>(d: DefConfig<T>): DefConfig<T> {
  return d;
}

const listaMontos = z
  .array(z.number().int().positive())
  .min(1)
  .refine((l) => new Set(l).size === l.length, 'Denominaciones repetidas');

/** Todo lo configurable del sistema (ERS §9.2): se edita en el módulo de Configuración. */
export const CONFIG_DEFS = {
  'clientes.origenes': def({
    categoria: 'Clientes',
    titulo: 'Cómo nos encontró',
    descripcion: 'Opciones que se ofrecen al registrar un cliente. Quitar una opción no cambia a los clientes que ya la tienen.',
    schema: origenesSchema,
    porDefecto: ORIGENES_POR_DEFECTO,
    editor: 'origenes',
    permisoEdicion: 'config.editar',
    validarCambio: (anterior, nuevo) =>
      anterior.every((a) => nuevo.some((n) => n.clave === a.clave)) ? null : 'No se puede borrar una opción; quítala (se desactiva)',
  }),
  'agenda.horizonteDias': def({
    categoria: 'Agenda',
    titulo: 'Anticipación máxima para agendar',
    descripcion: 'Días hacia adelante en los que se pueden agendar citas (2 meses ≈ 60 días).',
    schema: z.number().int().min(1).max(365),
    porDefecto: 60,
    editor: 'entero',
    unidad: 'días',
    permisoEdicion: 'config.editar',
  }),
  'agenda.toleranciaMinutos': def({
    categoria: 'Agenda',
    titulo: 'Tolerancia de llegada',
    descripcion: 'Minutos de espera tras marcar "No se ha presentado" antes de cancelar la cita automáticamente.',
    schema: z.number().int().min(1).max(120),
    porDefecto: 10,
    editor: 'entero',
    unidad: 'min',
    permisoEdicion: 'config.editar',
  }),
  'agenda.intervaloMinutos': def({
    categoria: 'Agenda',
    titulo: 'Intervalo de la agenda',
    descripcion: 'Cada cuántos minutos se ofrecen horarios para agendar.',
    schema: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20), z.literal(30)]),
    porDefecto: 15,
    editor: 'opciones',
    unidad: 'min',
    opciones: [5, 10, 15, 20, 30].map((v) => ({ valor: v, etiqueta: `${v} min` })),
    permisoEdicion: 'config.editar',
  }),
  'agenda.duracionSinBarberoMin': def({
    categoria: 'Agenda',
    titulo: 'Duración de citas sin barbero asignado',
    descripcion: 'Duración que se reserva cuando la cita aún no tiene barbero (la duración real depende del barbero).',
    schema: z.number().int().min(5).max(240),
    porDefecto: 30,
    editor: 'entero',
    unidad: 'min',
    permisoEdicion: 'config.editar',
  }),
  'agenda.horaAlertaSinBarbero': def({
    categoria: 'Agenda',
    titulo: 'Hora de la alerta de citas sin barbero',
    descripcion: 'Hora del día en que se avisa de las citas de mañana que no tienen barbero asignado.',
    schema: hora,
    porDefecto: '18:00',
    editor: 'hora',
    permisoEdicion: 'config.editar',
  }),
  'caja.tipoCambioUSD': def({
    categoria: 'Caja',
    titulo: 'Tipo de cambio del dólar',
    descripcion: 'Pesos mexicanos por 1 dólar (USD) usados al cobrar en dólares. Solo el dueño puede cambiarlo.',
    schema: z
      .number()
      .positive()
      .max(100)
      .refine((v) => Math.abs(v * 10_000 - Math.round(v * 10_000)) < 1e-6, 'Máximo 4 decimales'),
    porDefecto: 17,
    editor: 'decimal',
    unidad: 'MXN por USD',
    permisoEdicion: 'config.tipo_cambio',
  }),
  'caja.denominacionesMXN': def({
    categoria: 'Caja',
    titulo: 'Denominaciones en pesos',
    descripcion: 'Billetes y monedas en pesos que se cuentan en apertura, corte y cierre de caja.',
    schema: listaMontos,
    porDefecto: [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50],
    editor: 'lista_montos',
    permisoEdicion: 'config.editar',
  }),
  'caja.denominacionesUSD': def({
    categoria: 'Caja',
    titulo: 'Denominaciones en dólares',
    descripcion: 'Billetes y monedas en dólares que se cuentan en el arqueo de caja.',
    schema: listaMontos,
    porDefecto: [10000, 5000, 2000, 1000, 500, 100],
    editor: 'lista_montos',
    permisoEdicion: 'config.editar',
  }),
  'propinas.repartoSinDestinatario': def({
    categoria: 'Caja',
    titulo: 'Reparto de propinas sin destinatario',
    descripcion: 'Qué hacer con las propinas entregadas en recepción sin indicar para quién son.',
    schema: z.enum(['iguales_barberos_del_dia', 'sin_reparto']),
    porDefecto: 'iguales_barberos_del_dia' as 'iguales_barberos_del_dia' | 'sin_reparto',
    editor: 'opciones',
    opciones: [
      { valor: 'iguales_barberos_del_dia', etiqueta: 'Partes iguales entre los barberos que trabajaron ese día' },
      { valor: 'sin_reparto', etiqueta: 'No repartir (queda para el negocio)' },
    ],
    permisoEdicion: 'config.editar',
  }),
  'ticket.encabezado': def({
    categoria: 'Caja',
    titulo: 'Encabezado del ticket',
    descripcion: 'Texto que aparece debajo del logo en tickets y recibos.',
    schema: z.string().max(200),
    porDefecto: 'Barbones Barber',
    editor: 'texto',
    permisoEdicion: 'config.editar',
  }),
  'ticket.pie': def({
    categoria: 'Caja',
    titulo: 'Pie del ticket',
    descripcion: 'Mensaje al final del ticket.',
    schema: z.string().max(300),
    porDefecto: '¡Gracias por tu visita!',
    editor: 'texto',
    permisoEdicion: 'config.editar',
  }),
  'nomina.inicioSemana': def({
    categoria: 'Nómina',
    titulo: 'Inicio de la semana de pago',
    descripcion: 'Día en que inicia la semana para calcular comisiones y sueldos.',
    schema: z.union([z.literal(0), z.literal(1)]),
    porDefecto: 1 as 0 | 1,
    editor: 'opciones',
    opciones: [
      { valor: 1, etiqueta: 'Lunes' },
      { valor: 0, etiqueta: 'Domingo' },
    ],
    permisoEdicion: 'config.editar',
  }),
  'vacaciones.anticipacionDias': def({
    categoria: 'Personal',
    titulo: 'Anticipación para solicitar vacaciones',
    descripcion: 'Días mínimos de anticipación con que deben solicitarse las vacaciones.',
    schema: z.number().int().min(0).max(365),
    porDefecto: 30,
    editor: 'entero',
    unidad: 'días',
    permisoEdicion: 'config.editar',
  }),
  'inventario.alertasActivas': def({
    categoria: 'Inventario',
    titulo: 'Alertas de producto agotado',
    descripcion: 'Avisar cuando un producto llegue a su existencia mínima o se agote.',
    schema: z.boolean(),
    porDefecto: true,
    editor: 'booleano',
    permisoEdicion: 'config.editar',
  }),
  'cumpleanos.activo': def({
    categoria: 'Promociones',
    titulo: 'Descuento de cumpleaños',
    descripcion: 'Activa el descuento automático de cumpleaños (requiere fecha de nacimiento del cliente).',
    schema: z.boolean(),
    porDefecto: false,
    editor: 'booleano',
    permisoEdicion: 'config.editar',
  }),
  'cumpleanos.porcentaje': def({
    categoria: 'Promociones',
    titulo: 'Porcentaje del descuento de cumpleaños',
    descripcion: 'Porcentaje de descuento que se aplica en servicios durante la semana del cumpleaños.',
    schema: z.number().int().min(1).max(100),
    porDefecto: 10,
    editor: 'entero',
    unidad: '%',
    permisoEdicion: 'config.editar',
  }),
  'cumpleanos.ventanaDias': def({
    categoria: 'Promociones',
    titulo: 'Vigencia del descuento de cumpleaños',
    descripcion: 'Días antes y después del cumpleaños en que el descuento es válido.',
    schema: z.number().int().min(0).max(30),
    porDefecto: 3,
    editor: 'entero',
    unidad: 'días',
    permisoEdicion: 'config.editar',
  }),
  'retencion.purgaActiva': def({
    categoria: 'Datos',
    titulo: 'Purga automática por retención',
    descripcion: 'Elimina datos más antiguos que los plazos de conservación. Desactivada por defecto.',
    schema: z.boolean(),
    porDefecto: false,
    editor: 'booleano',
    permisoEdicion: 'config.editar',
  }),
  'retencion.reportesAnios': def({
    categoria: 'Datos',
    titulo: 'Conservación de información operativa',
    descripcion: 'Años que se conservan ventas, citas y movimientos para reportes.',
    schema: z.number().int().min(1).max(20),
    porDefecto: 4,
    editor: 'entero',
    unidad: 'años',
    permisoEdicion: 'config.editar',
  }),
  'retencion.usuariosAnios': def({
    categoria: 'Datos',
    titulo: 'Conservación de información de usuarios',
    descripcion: 'Años que se conserva la información de usuarios dados de baja.',
    schema: z.number().int().min(1).max(20),
    porDefecto: 5,
    editor: 'entero',
    unidad: 'años',
    permisoEdicion: 'config.editar',
  }),
} as const;

export type ClaveConfig = keyof typeof CONFIG_DEFS;
export type ValorConfig<K extends ClaveConfig> = (typeof CONFIG_DEFS)[K]['porDefecto'];
export type DefinicionConfig = (typeof CONFIG_DEFS)[ClaveConfig] & { clave: ClaveConfig };

export function esClaveConfig(clave: string): clave is ClaveConfig {
  return Object.prototype.hasOwnProperty.call(CONFIG_DEFS, clave);
}

export function buscarConfiguraciones(consulta: string): DefinicionConfig[] {
  return (Object.entries(CONFIG_DEFS) as [ClaveConfig, (typeof CONFIG_DEFS)[ClaveConfig]][])
    .filter(([clave, d]) => coincideBusqueda(`${clave} ${d.categoria} ${d.titulo} ${d.descripcion}`, consulta))
    .map(([clave, d]) => ({ ...d, clave }) as DefinicionConfig);
}

export function validarValorConfig(clave: ClaveConfig, valor: unknown) {
  return (CONFIG_DEFS[clave].schema as ZodType).safeParse(valor);
}

/** Representación serializable (sin esquema) para enviar a los clientes. */
export interface ConfigItem {
  clave: ClaveConfig;
  categoria: string;
  titulo: string;
  descripcion: string;
  editor: EditorConfig;
  unidad?: string;
  opciones?: { valor: string | number; etiqueta: string }[];
  permisoEdicion: Permiso;
  porDefecto: unknown;
  valor: unknown;
}
